import React, { useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import isEmail from 'validator/es/lib/isEmail';
import isMobilePhone from 'validator/es/lib/isMobilePhone';
import * as Yup from 'yup';
import { AlertContent, Button, EmailVerificationForm, FieldWrapper, FormError, Identity, MobileVerificationForm, Modal, ModalFactory } from 'components';
import { mobileLocale } from 'lib/Validations';
import { routeConstants } from 'routes';
import { clearRequest, selectAPIRequestErrors, selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { IStore } from 'store/types';
import { hasEmailRegistered, hasMobileRegistered, resendIdentityVerificationCodeRequest, selectCurrentUserIdentities, updateIdentityVerificationCodeRequest, updateSMSRequest, User } from 'store/User';
import { ChannelType, IApiErrorPayloadWithCode, IdentityType, RequestStatus } from 'types';
import { AccountSetupFooter, AccountSetupHeader } from './components';

const AccountSetupAddMissingIdentity = () => {
  const dispatch = useDispatch();
  const hasMobile = useSelector(hasMobileRegistered());
  const hasEmail = useSelector(hasEmailRegistered());
  const hasBothMobileAndEmail = hasMobile && hasEmail;
  const [contactState] = useState(hasMobile ? IdentityType.MOBILE : IdentityType.EMAIL);

  // Routing
  const history = useHistory();
  const nextStep = routeConstants.private.accountSetup.notifications;

  const status = useSelector((state: IStore) => selectAPIRequestStatus(state, User.UPDATE_SMS_REQUEST));
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.UPDATE_SMS_REQUEST));
  const verifyLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.UPDATE_IDENTITY_VERIFICATION_CODE_REQUEST));
  const resendVerifyLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.RESEND_IDENTITY_VERIFICATION_CODE_REQUEST));
  const identities = useSelector(selectCurrentUserIdentities());
  const apiErrors = useSelector((state: IStore) => selectAPIRequestErrors(state, User.UPDATE_SMS_REQUEST)) as IApiErrorPayloadWithCode;
  const apiErrorsVerify = useSelector((state: IStore) => selectAPIRequestErrors(state, User.UPDATE_IDENTITY_VERIFICATION_CODE_REQUEST)) as IApiErrorPayloadWithCode;
  const [hasResentPasscode, setHasResentPasscode] = useState<boolean>(false);

  // Data
  const getMessage =
    contactState === IdentityType.EMAIL
      ? 'Enter a mobile number to help recover your account should you lose access to your email.'
      : 'Enter an optional email to help recover your account should you lose access to your mobile number. You will be able to log in with this email too.';

  const updateIdentity = (identityValue: string, toastMessage?: string) => {
    dispatch(updateSMSRequest(identityValue, toastMessage));
  };

  const onVerifySubmit = (verificationCode: string) => {
    const newIdentity = (identities || []).find((id) => id.value === values.identity);

    if (newIdentity?.id) {
      dispatch(updateIdentityVerificationCodeRequest(newIdentity?.id, verificationCode, 'Recovery method added.'));
    }
  };

  const resendSms = () => {
    updateIdentity(values.identity, 'Verification code resent.');
    setHasResentPasscode(true);
  };

  const resendCall = () => {
    const newIdentity = identities.find((identity) => identity.value === values.identity);

    if (newIdentity) {
      dispatch(resendIdentityVerificationCodeRequest(newIdentity.id, 'Verification call initiated', ChannelType.CALL));
    }
  };

  const mobileValidationSchema = Yup.object().shape({
    identity: Yup.string()
      .required('Mobile number is required.')
      .test('isMobilePhone', 'Invalid mobile number.', (phoneNumber: string) => isMobilePhone(phoneNumber, mobileLocale)),
  });

  const emailValidationSchema = Yup.object().shape({
    identity: Yup.string()
      .required('Email is a required field')
      .test('IsEmail', 'Invalid email address.', (email: string) => isEmail(email)),
  });

  const { errors, dirty, touched, isValid, setFieldValue, handleSubmit, resetForm, values, handleBlur } = useFormik({
    initialValues: {
      identity: '',
    },
    onSubmit: (values: { identity: string }) => {
      updateIdentity(values.identity);
    },
    validateOnBlur: true,
    validationSchema: contactState === IdentityType.EMAIL ? mobileValidationSchema : emailValidationSchema,
    enableReinitialize: true,
  });
  const validateField = (field: 'identity') => Boolean(touched[field] && errors[field]);

  // Next Button
  // const [formComplete, setFormComplete] = useState<boolean>(false);

  // useEffect(() => {
  //   if (verifyStatus === RequestStatus.success) {
  //     setFormComplete(true);
  //   }
  // }, [verifyStatus, values.identity, dispatch]);

  const handleNext = () => {
    if (dirty && isValid) {
      setModalType('verify');
      handleSubmit();
    }
  };

  // Modal
  const [modalType, setModalType] = useState<string | undefined>(undefined);

  const [showModal, hideModal] = useModal(
    () => (
      <ModalFactory
        customModal
        isActive
        onClose={() => {
          hideModal();
          setModalType(undefined);
        }}
      >
        {modalType === 'verify' && (
          <Modal
            isActive
            onClose={() => {
              dispatch(clearRequest(User.UPDATE_SMS_REQUEST));
              hideModal();
              setHasResentPasscode(false);
              setModalType(undefined);
            }}
          >
            {contactState === IdentityType.EMAIL && (
              <MobileVerificationForm
                mobile={values.identity}
                isLoading={verifyLoading}
                isResendLoading={resendVerifyLoading}
                errorMessages={apiErrorsVerify?.errorMessages}
                onSubmit={onVerifySubmit}
                resendVerificationCode={resendSms}
                resendCall={resendCall}
                showCallButton={hasResentPasscode}
              />
            )}

            {contactState === IdentityType.MOBILE && (
              <EmailVerificationForm email={values.identity} isLoading={verifyLoading} isResendLoading={resendVerifyLoading} errorMessages={apiErrorsVerify?.errorMessages} onSubmit={onVerifySubmit} resendVerificationCode={resendSms} />
            )}
          </Modal>
        )}

        {modalType === 'skip' && (
          <AlertContent
            title={`Skip adding recovery ${contactState === IdentityType.EMAIL ? 'mobile number' : 'email'}?`}
            message="You can add this later."
            leftActionLabel="Skip"
            onLeftAction={() => {
              hideModal();
              setModalType(undefined);
              history.push(nextStep);
            }}
            rightActionLabel="Add Now"
            onRightAction={() => {
              hideModal();
              setModalType(undefined);
              resetForm();
            }}
          />
        )}
      </ModalFactory>
    ),
    [dispatch, values.identity, verifyLoading, resendVerifyLoading, apiErrorsVerify?.errorMessages, contactState, identities?.length, modalType]
  );

  useEffect(() => {
    if (status === RequestStatus.SUCCESS && modalType) {
      showModal();
    }
  }, [status, values.identity, modalType, showModal]);

  useEffect(() => {
    if (hasBothMobileAndEmail) history.push(routeConstants.private.rootPath);
  }, [hasBothMobileAndEmail, history]);

  return (
    <>
      <AccountSetupHeader message={getMessage} title={`Add ${contactState === IdentityType.EMAIL ? 'Mobile Number' : 'Email'} To Account`} />

      <form>
        <FieldWrapper hasError={!isValid} label={contactState === IdentityType.EMAIL ? 'Mobile Number' : 'Email'} message={validateField('identity') ? errors.identity : ''}>
          <Identity
            disabled={isLoading}
            id="identity"
            onAccept={(val: string) => {
              setFieldValue('identity', val);
            }}
            onBlur={handleBlur}
            placeholder={contactState === IdentityType.EMAIL ? 'Mobile Number' : 'Email'}
            type="text"
            {...(contactState === IdentityType.EMAIL ? { inputMode: 'numeric' } : {})}
            phoneOnly={contactState === IdentityType.EMAIL}
          />
        </FieldWrapper>

        <FieldWrapper>{apiErrors?.errorMessages && apiErrors?.errorMessages.map((error, index) => <FormError key={`error-message-${index}`}>{error}</FormError>)}</FieldWrapper>
      </form>

      <AccountSetupFooter>
        <Button appearance="primary" label="Continue" onClick={handleNext} size="large" disabled={!(isValid && dirty)} isProcessing={isLoading} />
      </AccountSetupFooter>
    </>
  );
};

export default AccountSetupAddMissingIdentity;
